import { Pagination } from '@mm/state/store-interfaces';
import AudiencesModel, {
  AudienceType,
  FilterItemOperatorTypes,
  FilterParams,
  TagFilterItem,
  TagsFilters,
} from '../model/AudiencesModel';
import { Contact } from './contacts';
import { SearchAttributes } from './leads';

export type MyLeadsInternalAttributes = {
  // snake case is disabled for /search because MyLeadsAttributes.variables need to be uppercase
  created_at?: [{ start_date?: string; end_date?: string }];
  full_name?: string;
  automations?: string[];
  age?: [string];
  source?: string[];
  gender?: string[];
  leadType?: string[];
  urls_visited?: string[];
  platform?: string[];
  time_on_site?: [AttributeWithComparisonOperator];
  num_of_clicks?: [AttributeWithComparisonOperator];
  web_page_views?: [AttributeWithComparisonOperator];
  scroll_depth?: [AttributeWithComparisonOperator];
  web_sessions_count?: [AttributeWithComparisonOperator];
  klaviyo_purchased?: [boolean | { filter: false; allow_nil: true }];
  tags?: string[];
};

type AttributeWithComparisonOperator = {
  [key: string]: string;
};

export type MyleadsRelationalOperatorType = 'lt' | 'gt' | 'eq';

export const MyleadsRelationalOperators = {
  LESS_THAN: 'lt',
  GREATER_THAN: 'gt',
  EQUAL: 'eq',
} as const;

export type MyleadsLogicOperatorType = 'and';
export const MyleadsLogicOperators = {
  AND: 'and',
} as const;

export type MyLeadsAttributes = {
  attributes?: MyLeadsInternalAttributes;
  excludedAttributes?: MyLeadsInternalAttributes;
  prefixAttributes?: MyLeadsInternalAttributes;
  excludedPrefixAttributes?: MyLeadsInternalAttributes;
  wildcardAttributes?: MyLeadsInternalAttributes;
  excludedWildcardAttributes?: MyLeadsInternalAttributes;
  variables?: SearchAttributes;
  excludedVariables?: SearchAttributes;
  prefixVariables?: SearchAttributes;
  excludedPrefixVariables?: SearchAttributes;
  wildcardVariables?: SearchAttributes;
  excludedWildcardVariables?: SearchAttributes;
};

export type MyLeadsSearchQuery = {
  query?: MyLeadsAttributes;
  exclude?: number[];
  page?: number;
  filterParams?: FilterParams;
  tagsFilters?: TagsFilters | TagFilterItem[]; // to BE we send TagsFilters, but locally work with TagFilterItem[]
};

export type MyLeadsCreateAudienceRequestParams = {
  leadIds?: number[];
  excludedLeadIds?: number[];
  query?: MyLeadsAttributes;
  esFilters?: MyLeadsAttributes;
  filterParams?: FilterParams;
  tagsFilters?: TagsFilters;
  limit?: number;
  exclude?: number[];
  page?: number;
  name?: string;
  type?: AudienceType;
};

export type MyLeadsCreateAudienceResponse = {
  audience: AudiencesModel;
};

export type MyLeadsState = {
  busy: boolean;
  searchOptions: MyLeadsAttributes;
  contacts: Contact[];
  pagination: Pagination | { total: number; page: number; perPage: number };
};

export type MyLeadsUrlsQueryType = 'prefix';

export enum MyLeadsFriendlyName {
  LD_BUS_OWNER = 'Business Owner',
  LD_CREDRATE = 'Credit Rating',
  LD_INCOMESTHH = 'Estimated Household Income',
  LD_INV_REAL = 'Real Estate Investor',
  LD_INV_STOCKS = 'Stocks and Bonds Investor',
  LD_OCCUPATION = 'Occupation',
  LD_FUEL = 'Home Fuel Type',
  LD_HOMEMKTVAL = 'Home Value',
  LD_PROPERTY_TYPE = 'Property type',
  LD_HOMEOWNRNT = 'Own or rent their home',
  LD_MUNICIPALITY = 'Municipality',
  LD_ADDRESS = 'Adress',
  LD_CITY = 'City',
  LD_STATE = 'State',
  LD_AI_COUNTY_NAME = 'County',
  LD_ZIP = 'Zip Code',
  LD_BEAUTY = 'Beauty and Cosmetics',
  LD_BKSMAG_BKS = 'Books',
  LD_CATOWNER = 'Cat Owner',
  LD_CH_GEN = 'Childrens Products',
  LD_COL_ANTIQ = 'Antiques',
  LD_COL_ARTS = 'Art',
  LD_COL_COIN = 'Coins',
  LD_DONATION = 'Charitable Donor',
  LD_SEX = 'Gender',
  LD_DIETING = 'Dieting/Weight Loss',
  LD_EDUCATION = 'Education Level',
  LD_NUMCHILD = 'Number of Children',
  LD_PRFL_GUN_CONTROL = 'Gun Control Supporters',
  LD_PRFL_LGBT_SUPPORT = 'LGBTQ+ Donor/Supporter',
  LD_PRFL_MARIJUANA_REFORM = 'Marijuana Supporter',
  LD_PRFL_MIL_SUPPORT = 'Military Supporters',
  LD_RELIGION = 'Religion',
  LD_SINGPARENT = 'Single Parent',
  LD_DOGOWNER = 'Dog Owner',
  LD_COL_SPORT = 'Sports Memorabilia',
  LD_MRTLSTATUS = 'Marital Status',
  LD_NETWORTH = 'Net Worth',
  LD_PARTY_CODE = 'Political Party',
  LD_BOATING = 'Boating/Sailing',
  LD_DON_ENVIR = 'Environmental Issues Donor',
  LD_EQUESTRIAN = 'Equestrian',
  LD_EXERCISE = 'General Exercise',
  LD_FISHING = 'Fishing',
  LD_FOOD_WINE = 'Food and Wine',
  LD_FOODSNAT = 'Natural Foods',
  LD_GAM_BOARD = 'Board Games',
  LD_GAM_CASINO = 'Sports Betting',
  LD_GAM_COMP = 'Computer Games',
  LD_GAM_VID = 'Video Games',
  LD_GAMING = 'Casino Gaming',
  LD_GARDNER = 'Gardening',
  LD_GOLF = 'Golfer',
  LD_MOTORCYCLE = 'Motorcycling',
  LD_NASCAR = 'NASCAR',
  LD_READER = 'Reader',
  LD_SCISPACE = 'Science/Space',
  LD_SCUBADIVER = 'Scuba Diving',
  LD_SMOKING = 'Smoker',
  LD_SNOWSKI = 'Ski/Snowboard',
  LD_SPEC_BASE = 'Baseball',
  LD_SPEC_HOCK = 'Ice Hockey',
  LD_SPEC_SOCC = 'Soccer',
  LD_TENNIS = 'Tennis',
  LD_TRAVELER = 'Traveler',
  LD_FIRSTNAME = 'First Name',
  LD_LASTNAME = 'Last Name',
  LD_EMAIL = 'Email',
  LD_PHONE_NUM = 'Phone Number',
  LD_AUTO_BODY = 'Auto Body Type',
  LD_AUTO_ECO = 'Auto is Eco Friendly',
  age = 'Age',
  gender = 'Gender',
  source = 'Source',
  full_name = 'Name',
  created_at = 'Date Created',
  email_send = 'Email Sent',
  email_click = 'Has clicked',
  email_introduce_to_sale = 'Intro to sales team',
  email_open = 'Has opened',
  email_reply = 'Has replied',
  sms_send = 'SMS Sent',
  time_on_site = 'Time on site',
  num_of_clicks = 'Number of clicks on site',
  web_page_views = 'Page views',
  scroll_depth = 'Scroll depth',
  no_interaction = 'No Interaction',
  web_sessions_count = 'Total visits (sessions)',
  BUSINESS_CONTACT = 'Business Leads',
  CONSUMER_LEADS = 'Consumer Leads',
  LANDING_PAGE_URL = 'Landing Page Url',
  urls_visited = 'Page browsed during visit',
  klaviyo_total_revenue = 'Klaviyo Orders Value',
  klaviyo_total_orders = 'Klaviyo Placed Orders',
  klaviyo_purchased = 'Klaviyo Purchase Recorded',
  shopify_total_orders = 'Shopify Placed Orders',
  shopify_total_revenue = 'Shopify Orders Value',
  shopify_purchased = 'Shopify Purchase Recorded',
  wday = 'Day of the week',
}

export const SubsectionKeys = {
  source: 'source',
  leadType: 'businessContact',
  landingPageUrl: 'landingPageUrl',
  busOwner: 'ldBusOwner',
  credRate: 'ldCredrate',
  income: 'ldIncomesthh',
  interactions: ['email_send', 'sms_send', 'no_interaction'],
  intent: [
    'email_click',
    'email_open',
    'email_reply',
    'email_introduce_to_sale',
  ],
  revenueAttributes: ['klaviyo_purchased', 'klaviyo_total_revenue'],
  customerJourney: [
    'time_on_site',
    'num_of_clicks',
    'web_page_views',
    'scroll_depth',
    'web_sessions_count',
  ],
  occupation: 'ldOccupation',
  profHobby: ['ldInvReal', 'ldInvStocks'],
  homeValue: 'ldHomemktval',
  homeFuel: 'ldFuel',
  homeOwn: 'ldHomeownrnt',
  location: ['ldCity', 'ldZip', 'ldState', 'ldAiCountyName', 'ldMunicipality'],
  lifestyle: [
    'ldColAntiq',
    'ldColArts',
    'ldBeauty',
    'ldBksmagBks',
    'ldCatowner',
    'ldDonation',
    'ldChGen',
    'ldColCoin',
    'ldDogowner',
    'ldColSport',
  ],
  age: 'age',
  education: 'ldEducation',
  gender: 'gender',
  lgbtSupport: 'ldPrflLgbtSupport',
  mrtlStatus: 'ldMrtlstatus',
  numChild: 'ldNumchild',
  netWorth: 'ldNetworth',
  partyCode: 'ldPartyCode',
  religion: 'ldReligion',
  personal: [
    'ldDieting',
    'ldPrflGunControl',
    'ldPrflMarijuanaReform',
    'ldPrflMilSupport',
    'ldSingparent',
  ],
  interests: [
    'ldDonEnvir',
    'ldFoodWine',
    'ldFoodsnat',
    'ldGamBoard',
    'ldGamCasino',
    'ldGamComp',
    'ldGamVid',
    'ldGaming',
    'ldMotorcycle',
    'ldScispace',
    'ldScubadiver',
    'ldSmoking',
  ],
  sports: [
    'ldSpecBase',
    'ldBoating',
    'ldSpecHock',
    'ldGolf',
    'ldNascar',
    'ldSnowski',
    'ldSpecSocc',
    'ldTennis',
  ],
  urlsVisited: 'urls_visited',
  platforms: 'platform',
  tags: 'tags',
};

export enum MyLeadsFilterTypes {
  attributes = 'attributes',
  excludedAttributes = 'excludedAttributes',
  prefixAttributes = 'prefixAttributes',
  excludedPrefixAttributes = 'excludedPrefixAttributes',
  wildcardAttributes = 'wildcardAttributes',
  excludedWildcardAttributes = 'excludedWildcardAttributes',
  variables = 'variables',
  excludedVariables = 'excludedVariables',
  prefixVariables = 'prefixVariables',
  excludedPrefixVariables = 'excludedPrefixVariables',
  wildcardVariables = 'wildcardVariables',
  excludedWildcardVariables = 'excludedWildcardVariables',
}

export const myLeadsIncludeTypes = [
  MyLeadsFilterTypes.attributes,
  MyLeadsFilterTypes.prefixAttributes,
  MyLeadsFilterTypes.wildcardAttributes,
  MyLeadsFilterTypes.variables,
  MyLeadsFilterTypes.prefixVariables,
  MyLeadsFilterTypes.wildcardVariables,
];
export const myLeadsExcludeTypes = [
  MyLeadsFilterTypes.excludedAttributes,
  MyLeadsFilterTypes.excludedPrefixAttributes,
  MyLeadsFilterTypes.excludedWildcardAttributes,
  MyLeadsFilterTypes.excludedVariables,
  MyLeadsFilterTypes.excludedPrefixVariables,
  MyLeadsFilterTypes.excludedWildcardVariables,
];

export const myLeadsAttributesBasedTypes = [
  MyLeadsFilterTypes.attributes,
  MyLeadsFilterTypes.prefixAttributes,
  MyLeadsFilterTypes.wildcardAttributes,
  MyLeadsFilterTypes.excludedAttributes,
  MyLeadsFilterTypes.excludedPrefixAttributes,
  MyLeadsFilterTypes.excludedWildcardAttributes,
];

export const startsWithTypes = [
  MyLeadsFilterTypes.prefixVariables,
  MyLeadsFilterTypes.excludedPrefixVariables,
  MyLeadsFilterTypes.prefixAttributes,
  MyLeadsFilterTypes.excludedPrefixAttributes,
];

export const containsTypes = [
  MyLeadsFilterTypes.wildcardAttributes,
  MyLeadsFilterTypes.wildcardVariables,
  MyLeadsFilterTypes.excludedWildcardVariables,
  MyLeadsFilterTypes.excludedWildcardAttributes,
];

export const MY_LEADS_OPERATORS_BY_TYPE = {
  [MyLeadsFilterTypes.attributes]: FilterItemOperatorTypes.EQUAL,
  [MyLeadsFilterTypes.variables]: FilterItemOperatorTypes.EQUAL,
  [MyLeadsFilterTypes.prefixVariables]: FilterItemOperatorTypes.STARTS_WITH,
  [MyLeadsFilterTypes.prefixAttributes]: FilterItemOperatorTypes.STARTS_WITH,
  [MyLeadsFilterTypes.excludedVariables]: FilterItemOperatorTypes.NOT_EQUAL,
  [MyLeadsFilterTypes.excludedAttributes]: FilterItemOperatorTypes.NOT_EQUAL,
  [MyLeadsFilterTypes.excludedPrefixVariables]:
    FilterItemOperatorTypes.STARTS_WITH,
  [MyLeadsFilterTypes.excludedPrefixAttributes]:
    FilterItemOperatorTypes.STARTS_WITH,
  [MyLeadsFilterTypes.wildcardVariables]: FilterItemOperatorTypes.CONTAINS,
  [MyLeadsFilterTypes.wildcardAttributes]: FilterItemOperatorTypes.CONTAINS,
  [MyLeadsFilterTypes.excludedWildcardVariables]:
    FilterItemOperatorTypes.DOESN_NOT_CONTAIN,
  [MyLeadsFilterTypes.excludedWildcardAttributes]:
    FilterItemOperatorTypes.DOESN_NOT_CONTAIN,
};
